import {
  ApplicationInsights,
  SeverityLevel,
} from "@microsoft/applicationinsights-web";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ApplicationGeneralEvents,
  DashboardEvents,
  ExternalProductRequestEvents,
  FinancingSolutionEvents,
  GenericProductRequestEvents,
  KycEvents,
  OnboardingEvents,
  OpenbankingEvents,
  PartnerOnboardingEvents,
  PartnerProductRequestEvents,
  ProductRequestEvents,
  ProductSelectorEvents,
  SystemEvents,
  UserProfileEvents,
} from "./AppInsightEvents";

import { useHistory, useLocation } from "react-router-dom";
import {
  getUserAnalyticsContext,
  UserAnalyticsContextData,
} from "../api/AnalyticsService";
import config from "../api/config";
import { pushInfoToDataLayer } from "../utils/utils";

declare global {
  interface Window {
    analytics?: {
      events?: any;
      trackEvent?: any;
      trackException?: any;
    };
    appInsightEvents?: any;
    dataLayer?: any[];
  }
}

const initialState: { [key: string]: any } = {
  loading: false,
};

export const AnalyticsContext = createContext(initialState);

export const useAnalyticsContext = () => {
  return useContext<any>(AnalyticsContext);
};

interface Props {
  children: React.ReactNode;
}

interface UserInfo {
  cui?: string;
  email?: string;
  phoneNumber?: string;
  fullName?: string;
  uuid?: string;
  sessionId?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  partner?: string;
}

window.analytics = window.analytics || {};

window.analytics = {
  events: {
    ...OnboardingEvents,
    ...DashboardEvents,
    ...ExternalProductRequestEvents,
    ...GenericProductRequestEvents,
    ...ProductRequestEvents,
    ...KycEvents,
    ...SystemEvents,
    ...UserProfileEvents,
    ...ProductSelectorEvents,
    ...FinancingSolutionEvents,
    ...PartnerOnboardingEvents,
    ...PartnerProductRequestEvents,
    ...ApplicationGeneralEvents,
    ...OpenbankingEvents,
  },
};

const NOT_APPLIED = "not_applied";

const buildDataLayerUserData = (context: UserAnalyticsContextData) => ({
  loggedStatus: "logged",
  userId: context.userId,
  md5: context.emailMd5,
  sha256: context.emailSha256,
  loans: context.loansCount,
  loansAmount: context.totalLoansGrantedAmount ?? NOT_APPLIED,
  late_installments: context.hasBadPaymentBehavior ?? NOT_APPLIED,
  last_product_code: context.lastProductRequestCode ?? NOT_APPLIED,
  last_product_status: context.lastProductRequestStatus ?? NOT_APPLIED,
  traffic_source: context.userSource,
});

export const AnalyticsProvider = ({ children }: Props) => {
  const [appInsightsClient, setAppInsightsClient] =
    useState<ApplicationInsights>();
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [userAnalyticsContext, setUserAnalyticsContext] =
    useState<UserAnalyticsContextData>();
  const isFetchingAnalyticsContextRef = useRef(false);
  const history = useHistory();
  const location = useLocation();

  const fetchAndSetUserAnalyticsContext = async (token: string) => {
    if (
      process.env.REACT_APP_GTM_ENABLED !== "true" ||
      userAnalyticsContext ||
      isFetchingAnalyticsContextRef.current
    ) {
      return;
    }
    isFetchingAnalyticsContextRef.current = true;
    try {
      const contextData = await getUserAnalyticsContext(token);
      setUserAnalyticsContext(contextData);
    } catch (error: any) {
      window.analytics?.trackEvent?.(
        window.analytics.events.GetUserAnalyticsContextError,
        error,
      );
    } finally {
      isFetchingAnalyticsContextRef.current = false;
    }
  };

  const updateUserInfo = (newInfo: UserInfo) => {
    const updatedUserInfo = { ...userInfo, ...newInfo };
    setUserInfo(updatedUserInfo);
  };

  ///Initialize app insights
  useEffect(() => {
    if (process.env.REACT_APP_APP_INSIGHTS_INSTRUMENTATION_KEY) {
      const client = new ApplicationInsights({
        config: {
          instrumentationKey:
            process.env.REACT_APP_APP_INSIGHTS_INSTRUMENTATION_KEY,
        },
      });
      client.loadAppInsights();

      setAppInsightsClient(client);
    }
  }, []);

  ///set proper operation_name in tags
  useEffect(() => {
    appInsightsClient?.addTelemetryInitializer((e) => {
      e.tags!["ai.operation.name"] = history.location.pathname;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, appInsightsClient]);

  useEffect(() => {
    if (userAnalyticsContext) {
      pushInfoToDataLayer({
        userData: buildDataLayerUserData(userAnalyticsContext),
      });
    }
  }, [userAnalyticsContext]);

  ///create RouteChanged event, with action (POP = back button!!!)
  useEffect(() => {
    appInsightsTrackEvent(window.analytics?.events.RouteChanged, {
      action: history.action,
    });
    if (userAnalyticsContext) {
      pushInfoToDataLayer({
        userData: buildDataLayerUserData(userAnalyticsContext),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.action, location]);

  const appInsightsTrackEvent = (
    eventName: string,
    eventCustomProperties: any,
  ) => {
    let enrichedProperties = {
      ...eventCustomProperties,
    };
    if (userInfo) {
      enrichedProperties = {
        ...enrichedProperties,
        ...{
          cui: userInfo?.cui,
          email: userInfo?.email?.toLowerCase(),
          phone: userInfo?.phoneNumber,
          fullName: `${userInfo?.firstName} ${userInfo?.lastName}`,
          clientSessionId: config.sessionId,
          pathname: location.pathname,
          partner: userInfo?.partner,
          //existing user
          userId: userInfo?.userId,
        },
      };
    }
    appInsightsClient?.trackEvent({
      name: eventName,
      properties: enrichedProperties,
    });
  };

  const appInsightsTrackException = (error?: any) => {
    console.info("Tracking exception !!!!!!!!", error, SeverityLevel.Error);
    appInsightsClient?.trackException({
      exception: new Error(error),
      severityLevel: SeverityLevel.Error,
    });
  };

  window.analytics = {
    ...window.analytics,
    trackEvent: appInsightsTrackEvent,
    trackException: appInsightsTrackException,
  };

  return (
    <AnalyticsContext.Provider
      value={{
        updateUserInfo,
        appInsightsClient,
        userInfo,
        userAnalyticsContext,
        fetchAndSetUserAnalyticsContext,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};
